/* eslint-disable @typescript-eslint/no-unsafe-argument */
import type { GetStaticProps, GetStaticPaths, NextPage } from 'next'
import { type ISbStoryData } from 'storyblok-js-client'
import { Map } from '~/components/Map/MapMapbox'
import { mapBussinessFinder } from '~/components/BusinessFinder/BusinessFinder.mapping'
import { Layout } from '~/components/Layout/Layout'
import { mapLayout } from '~/components/Layout/Layout.mapping'
import {
  mapSearchResults,
  type mapppedMapProps
} from '~/components/Map/Map.mapping'
import { PageMetadata } from '~/components/PageMetadata/PageMetadata'
import { type PageMetadataProps } from '~/components/PageMetadata/PageMetadata.interface'
import { mapPageMetadata } from '~/components/PageMetadata/PageMetadata.mapping'
import {
  fetchBusinessesContent,
  getAllStoryblokLinks,
  fetchAllStoryblokContent
} from '~/lib/storyblok'
import { type ISbStory } from '~/types'
import {
  type GlobalSettingsStoryblok,
  type BusinessDetailStoryblok
} from '~/types/generated'
import {
  mapFilters,
  type ParamsProps
} from '~/components/BusinessListingFilters/BusinessListingFilters.mapping'

export interface BusinessListingPageProps extends PageMetadataProps {
  story: ISbStory;
  settings: ISbStory;
  layout: ISbStory;
  finder: ISbStory;
  search: ISbStory;
}

const BusinessListingPage: NextPage<BusinessListingPageProps> = (
  ctx
): JSX.Element => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { metadata, layout, finder, search, schema } = ctx

  // const metaImage = search.results.businessListings[0].image.src || null

  if (!layout) {
    return <div />
  }

  return (
    <>
      <PageMetadata
        metadata={metadata}
        // priorityImage={metaImage}
        schema={schema}
      />
      <Layout layout={layout} businessFinder={finder} pinkHeader>
        <main>
          <Map
            filters={search.results.filters}
            businessListings={search.results.businessListings}
            markers={search.results.markers}
            mapSrc={search.results.mapSrc}
            attribution={search.results.attribution}
            zoom={search.results.zoom}
            center={search.results.center}
            activeBusinessId={search.results.activeBusinessId}
            activeBusinessName={search.results.activeBusinessName}
            footer={layout.footer}
            businessPage={!search.results.filters}
          />
        </main>
      </Layout>
    </>
  )
}

export const getStaticProps: GetStaticProps = async (context) => {
  const { params } = context

  if (!params || !('slug' in params)) {
    return { notFound: true }
  }

  const slug = Array.isArray(params.slug) ? params.slug[0] : params.slug
  if (typeof slug !== 'string') {
    return { notFound: true }
  }

  const type = Array.isArray(params.type) ? params.type[0] : params.type
  if (typeof type !== 'string') {
    return { notFound: true }
  }

  if (type.includes('.') || slug.includes('.')) {
    return { notFound: true }
  }

  if (slug.includes('&') || slug.includes('?')) {
    const cleanSlug = slug.split(/[&?]/)[0]
    return {
      redirect: {
        destination: `/businesses/${type}/${cleanSlug}`,
        permanent: false
      }
    }
  }

  const version = context.draftMode ? 'draft' : 'published'

  const getStoryblokStories = await fetchAllStoryblokContent({
    by_slugs: `global-settings,businesses/${type}/${slug}`,
    resolve_relations: [
      'globalSettings.footerNavigation',
      'globalSettings.advert'
    ],
    version
  })

  const globalSettingsData = getStoryblokStories.find(
    (content: { slug: string }) => content.slug === 'global-settings'
  )
  const pageData = getStoryblokStories.find(
    (content: { slug: string }) => content.slug === slug
  )

  if (!globalSettingsData || !pageData) {
    return { notFound: true }
  }

  const businessesData = await fetchBusinessesContent({
    version: 'published'
  })

  if (!businessesData) {
    return { notFound: true }
  }

  const businessCategories = businessesData.map(
    (bussiness: ISbStory) => bussiness.content.businessCategory[0]
  )

  const finder = mapBussinessFinder(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    businessCategories,
    globalSettingsData.content as GlobalSettingsStoryblok
  )

  const layout = mapLayout(
    globalSettingsData.content as GlobalSettingsStoryblok,
    true
  )

  const metadata = mapPageMetadata(
    pageData.content.seoMetadata[0],
    `businesses/${type}/${slug}`,
    globalSettingsData.content as GlobalSettingsStoryblok
  )

  const businessId = pageData.id.toString()

  const mapBusinessStoryToSearchDocument = (
    businessStory: ISbStoryData<BusinessDetailStoryblok>
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  ) => {
    const businessContent = businessStory.content
    const businessSlug = businessStory.slug
    const businessInfo = businessContent.businessInfo[0]
    const businessCategory = businessContent.businessCategory[0]
    const businessImages = businessContent.businessImages[0]
    const kebabCaseComponent = businessCategory.component
      .replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2')
      .toLowerCase()

    const businessFilters: ParamsProps = {
      type: kebabCaseComponent,
      categories: businessCategory.secondaryCategories as string[]
    }

    const filters = mapFilters(businessCategories, businessFilters)

    return {
      id: businessStory.id.toString(),
      name: businessInfo.businessName,
      short_description: businessInfo.shortDescription,
      long_description: businessInfo.longDescription,
      address: businessInfo.address,
      url: businessInfo?.url?.url ?? '',
      telephone: businessInfo.telephoneNumber ?? '',
      business_type: filters.type,
      categories: filters.categories,
      category_text: businessCategory?.pluralisationText ?? '',
      latitude: businessContent.mapLatitude ?? '',
      longitude: businessContent.mapLongitude ?? '',
      primary_image_url: businessImages.primaryImage.filename,
      secondary_image_url: businessImages.secondaryImage?.filename ?? '',
      tertiary_image_url: businessImages.tertiaryImage?.filename ?? '',
      slug: businessSlug
    }
  }

  const searchDocument = mapBusinessStoryToSearchDocument(pageData)

  const searchData = {
    results: [searchDocument]
  }

  const mappedSearchData = mapSearchResults(
    searchData as mapppedMapProps,
    null,
    businessId,
    null,
    null
  )

  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: searchDocument.name,
    description: searchDocument.long_description,
    image: searchDocument.primary_image_url,
    telephone: searchDocument.telephone,
    url: metadata.canonical,
    sameAs: searchDocument.url,
    address: {
      '@type': 'PostalAddress',
      streetAddress: searchDocument.address
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: searchDocument.latitude,
      longitude: searchDocument.longitude
    }
  }

  return {
    props: {
      metadata,
      story: pageData,
      layout,
      finder,
      schema,
      search: {
        businessId,
        categories: businessCategories,
        results: mappedSearchData
      }
    }
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  const links = await getAllStoryblokLinks()

  const paths: Array<{ params: { slug: string[]; type: string[] } }> = []
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  Object.keys(links).forEach((linkKey) => {
    if (
      !!links[linkKey].is_folder ||
      links[linkKey].slug === 'home' ||
      links[linkKey].slug === 'global-settings' ||
      links[linkKey].slug === 'business-listing' ||
      links[linkKey].slug === 'search' ||
      links[linkKey].slug === 'cookie-policy' ||
      links[linkKey].slug === 'terms-conditions' ||
      links[linkKey].slug === 'privacy-policy' ||
      links[linkKey].slug === 'get-listed'
    ) {
      return
    }

    const { slug } = links[linkKey]
    const splittedSlug = slug.split('/')

    if (splittedSlug.length !== 3) {
      return
    }

    paths.push({ params: { type: splittedSlug[1], slug: splittedSlug[2] } })
  })

  return {
    paths,
    fallback: 'blocking'
  }
}

export default BusinessListingPage
